// frontend/src/hooks/useAuth.tsx

import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { MOCK_USER, updateMockProfileInStorage } from '../utils/mockUser';
import { userService } from '../api/userService';
import type { UserProfile } from '../types';
import { msalInitPromise, msalInstance } from '../index';
import { apiScopes } from '../authConfig';
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { ensureApiReady } from '../api/apiClient';
import { ApiError } from '../api/apiClient';
import { recordAuthProfile } from '../utils/authInsights';
import { setAnalyticsProductContext, trackSignUp } from '../utils/gaEvents';

type AuthContextValue = {
  userProfile: UserProfile | null;
  isAuthenticated: boolean;
  loading: boolean;
  user: any;
  getAccessToken: () => Promise<string | null>;
  /** Resolves true when profile loaded; false on failure (401 clears session). */
  refreshProfile: () => Promise<boolean>;
  updateUserLimit: (newLimit: number) => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

/** Entra / MSAL errors that mean the cached account is no longer usable silently. */
/** Keep deadMarkers in sync with api/src/utils/authContinuity.js SESSION_DEAD_MARKERS. */
function isSessionDeadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  if (error instanceof InteractionRequiredAuthError) return true;

  const err = error as { name?: string; errorCode?: string; code?: string; message?: string };
  const code = String(err.errorCode || err.code || '');
  const message = String(err.message || '');
  const haystack = `${code} ${message}`.toLowerCase();

  const deadMarkers = [
    'monitor_window_timeout',
    'login_required',
    'interaction_required',
    'consent_required',
    'token_renewal_error',
    'no_tokens_found',
    'invalid_grant',
    'iframe_closed_prematurely',
    'post_request_failed',
    'endpoints_resolution_error',
  ];
  return deadMarkers.some((m) => haystack.includes(m));
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { instance, accounts } = useMsal();
  const msalIsAuthenticated = useIsAuthenticated();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const clearingRef = useRef(false);

  /**
   * Drop stale MSAL cache so UI matches reality (guest chrome, ProtectedRoute bounce).
   * No redirect — Layout login_request / user click can start Entra again.
   */
  const clearStaleSession = useCallback(async (reason?: string) => {
    if (clearingRef.current) return;
    clearingRef.current = true;
    try {
      console.warn('[auth] Clearing stale session:', reason || 'unknown');
      recordAuthProfile(false, { status: 401 });
      setIsAuthenticated(false);
      setUser(null);
      setUserProfile(null);
      try {
        instance.setActiveAccount(null);
        await instance.clearCache();
      } catch (cacheErr) {
        console.warn('[auth] MSAL clearCache failed:', cacheErr);
      }
    } finally {
      clearingRef.current = false;
    }
  }, [instance]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (process.env.NODE_ENV === 'development' && localStorage.getItem('mockUser') === 'true') {
      return 'MOCK_TOKEN';
    }

    try {
      await msalInitPromise;
    } catch {
      return null;
    }

    let account = msalInstance.getActiveAccount();
    if (!account) {
      const all = msalInstance.getAllAccounts();
      if (all[0]) {
        msalInstance.setActiveAccount(all[0]);
        account = all[0];
      }
    }
    if (!account) return null;

    const tokenRequest = { scopes: apiScopes, account };

    try {
      const response = await msalInstance.acquireTokenSilent(tokenRequest);
      return response.accessToken;
    } catch (error: unknown) {
      // Do not acquireTokenPopup after idle — iframe/popup timeouts leave "logged in" chrome
      // with no API token. Clear cache and let ProtectedRoute / login_request re-auth.
      if (isSessionDeadError(error)) {
        await clearStaleSession(
          (error as { errorCode?: string }).errorCode
            || (error as { message?: string }).message
            || 'silent_token_failed'
        );
        return null;
      }
      console.error('Silent token acquisition failed:', error);
      return null;
    }
  }, [clearStaleSession]);

  const refreshProfile = useCallback(async (): Promise<boolean> => {
    try {
      const token = await getAccessToken();
      if (!token) {
        recordAuthProfile(false, { status: 401 });
        setUserProfile(null);
        return false;
      }
      const profile = await userService.getUserProfile(token);
      setUserProfile(profile);
      recordAuthProfile(true);
      // Primary acquisition conversion for Google Ads Smart Bidding (not a click).
      if (profile?.accountCreated) {
        trackSignUp({ method: 'entra', plan: profile.plan || 'Free' });
      }
      return true;
    } catch (error) {
      console.error('Error refreshing profile:', error);
      recordAuthProfile(false, error);
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        await clearStaleSession(`profile_http_${error.status}`);
      }
      return false;
    }
  }, [getAccessToken, clearStaleSession]);

  const updateUserLimit = useCallback((newLimit: number) => {
    setUserProfile(prev => prev ? { ...prev, queriesLeft: newLimit } : prev);
    if (process.env.NODE_ENV === 'development' && localStorage.getItem('mockUser') === 'true') {
      updateMockProfileInStorage({ queriesLeft: newLimit });
    }
  }, []);

  useEffect(() => {
    // Guests must not be labeled plan=Free — that collapses Guest→signup vs Free→paywall in GA4.
    setAnalyticsProductContext({
      plan: isAuthenticated ? (userProfile?.plan || 'Free') : 'Guest',
      auth_state: isAuthenticated ? (userProfile ? 'ready' : 'profile_pending') : 'guest',
    });
  }, [isAuthenticated, userProfile]);

  // Start ACA wake as soon as the shell mounts (DEV minReplicas=0).
  useEffect(() => {
    void ensureApiReady();
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      setLoading(true);
      let isAuth = false;
      let account: any = null;

      if (process.env.NODE_ENV === 'development') {
        const mockUserStatus = localStorage.getItem('mockUser');
        if (mockUserStatus === 'true') {
          isAuth = true;
          account = MOCK_USER;
        }
      }

      if (!isAuth) {
        const hasMsalAccount = msalIsAuthenticated || accounts.length > 0;
        if (hasMsalAccount) {
          if (!instance.getActiveAccount() && accounts[0]) {
            instance.setActiveAccount(accounts[0]);
          }
          // Cached Entra account ≠ usable session. Prove with silent token.
          const token = await getAccessToken();
          if (token) {
            isAuth = true;
            account = instance.getActiveAccount() || accounts[0] || null;
          } else {
            // getAccessToken already clears on session-dead; ensure guest UI either way
            isAuth = false;
            account = null;
            if (instance.getAllAccounts().length > 0) {
              await clearStaleSession('boot_no_access_token');
            }
          }
        }
      }

      setIsAuthenticated(isAuth);
      setUser(account);

      if (isAuth) {
        await refreshProfile();
      } else {
        setUserProfile(null);
      }

      setLoading(false);
    };

    void checkAuth();
  }, [msalIsAuthenticated, accounts, instance, getAccessToken, refreshProfile, clearStaleSession]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      (window as any).login = () => {
        localStorage.setItem('mockUser', 'true');
        window.location.reload();
      };
      (window as any).logout = () => {
        localStorage.setItem('mockUser', 'false');
        localStorage.removeItem('mock_user_db');
        window.location.reload();
      };
    }
  }, []);

  return (
    <AuthContext.Provider value={{
      userProfile, isAuthenticated, loading, user,
      getAccessToken, refreshProfile, updateUserLimit
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
